/**
 * SVG path geometry for the charts.
 *
 * The design draws its charts by hand rather than pulling in a charting
 * library — the shapes are simple and the visual language is specific
 * (glow filters, non-scaling strokes, area gradients). Keeping the maths here
 * means the components stay declarative and there's no runtime dependency.
 */

export type LinePaths = { line: string; area: string };

/**
 * Line and filled-area paths through `values`, scaled to fit a viewBox.
 * `padTop`/`padBottom` keep the stroke and its glow inside the box.
 */
export function linePaths(
  values: number[],
  width: number,
  height: number,
  padTop = 14,
  padBottom = 8,
): LinePaths {
  if (values.length === 0) return { line: '', area: '' };
  if (values.length === 1) {
    const y = height / 2;
    return {
      line: `M 0 ${y} L ${width} ${y}`,
      area: `M 0 ${y} L ${width} ${y} L ${width} ${height} L 0 ${height} Z`,
    };
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;

  // Pad the domain so a flat-ish series doesn't hug the top edge, and a truly
  // flat one renders as a centred horizontal line instead of dividing by zero.
  const domainMax = max + span * 0.08;
  const domainMin = min - span * 0.25;
  const domainSpan = domainMax - domainMin || 1;

  const dx = width / (values.length - 1);
  const plotHeight = height - padTop - padBottom;
  const yFor = (v: number) => padTop + (1 - (v - domainMin) / domainSpan) * plotHeight;

  const points = values.map((v, i) => [i * dx, yFor(v)] as const);
  const line = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(' ');

  return { line, area: `${line} L ${width} ${height} L 0 ${height} Z` };
}

/** Polyline `points` attribute for the small sparklines on the KPI cards. */
export function sparklinePoints(values: number[], width = 120, height = 26): string {
  if (values.length === 0) return '';
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const dx = values.length > 1 ? width / (values.length - 1) : width;
  return values
    .map((v, i) => `${(i * dx).toFixed(1)},${(height - 3 - ((v - min) / span) * (height - 6)).toFixed(1)}`)
    .join(' ');
}

export type BarPair = {
  key: string;
  x1: number;
  x2: number;
  width: number;
  y1: number;
  h1: number;
  y2: number;
  h2: number;
};

/** Paired income/expense bars for the Analytics chart. */
export function pairedBars(
  data: Array<{ key: string; a: number; b: number }>,
  width: number,
  height: number,
  padTop = 14,
  padBottom = 26,
): BarPair[] {
  if (data.length === 0) return [];
  const max = Math.max(1, ...data.flatMap((d) => [d.a, d.b])) * 1.1;
  const slot = width / data.length;
  const barWidth = Math.max(4, Math.min(15, slot / 2.6));
  const base = height - padBottom;
  const span = base - padTop;

  return data.map((d, i) => {
    const centre = i * slot + slot / 2;
    const h1 = (d.a / max) * span;
    const h2 = (d.b / max) * span;
    return {
      key: d.key,
      x1: centre - barWidth - 2,
      x2: centre + 2,
      width: barWidth,
      y1: base - h1,
      h1,
      y2: base - h2,
      h2,
    };
  });
}

/** Evenly spaced horizontal gridlines. */
export function gridLines(count: number, height: number, padTop = 14, padBottom = 26): number[] {
  const span = height - padTop - padBottom;
  return Array.from({ length: count }, (_, i) => padTop + (i * span) / (count - 1));
}

/**
 * `stroke-dasharray` for a gauge drawn with `pathLength="100"`, so the arc is
 * expressed directly as a percentage.
 */
export function gaugeDash(percent: number): string {
  return `${Math.max(0, Math.min(100, percent))} 100`;
}
